import React, { useState, useEffect } from 'react';
import { Star, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EncuestaPage() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    nombre: '',
    calificacion: 0,
    que_gusto: '',
    que_mejorar: '',
    mensaje: ''
  });
  const [enviado, setEnviado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);

  // Pre-llenar desde URL
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const ratingParam = searchParams.get('rating');
    
    if (emailParam || ratingParam) {
      setFormData(prev => ({
        ...prev,
        email: emailParam || prev.email,
        calificacion: ratingParam ? parseInt(ratingParam) : prev.calificacion
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.calificacion === 0) {
      alert('Por favor selecciona una calificación');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/encuesta/responder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setEnviado(true);
      } else {
        alert('Error al enviar. Intenta de nuevo.');
      }
    } catch (error) {
      alert('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  if (enviado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center border border-white/20">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-400" size={48} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">¡Gracias!</h1>
          <p className="text-slate-300 mb-6">Tu opinión es muy valiosa para nosotros. Nos ayuda a mejorar LILA cada día.</p>
          <a 
            href="https://legal.linorttech.com"
            className="inline-block bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all"
          >
            Volver a LILA
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">⚖️ LILA</h1>
          <p className="text-purple-300">Tu opinión nos importa</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">¿Qué te pareció LILA?</h2>
            <p className="text-slate-400">Ayúdanos a mejorar con tu feedback</p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Tu email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="tu@email.com"
            />
          </div>

          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Tu nombre (opcional)</label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Tu nombre"
            />
          </div>

          {/* Calificación con estrellas */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">¿Cómo calificarías tu experiencia? *</label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, calificacion: star })}
                  onMouseEnter={() => setHoverStar(star)}
                  onMouseLeave={() => setHoverStar(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={40}
                    className={`transition-colors ${
                      star <= (hoverStar || formData.calificacion)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-slate-600'
                    }`}
                  />
                </button>
              ))}
            </div>
            {formData.calificacion > 0 && (
              <p className="text-center text-sm text-slate-400 mt-2">
                {formData.calificacion === 1 && 'Muy malo 😞'}
                {formData.calificacion === 2 && 'Malo 😕'}
                {formData.calificacion === 3 && 'Regular 😐'}
                {formData.calificacion === 4 && 'Bueno 😊'}
                {formData.calificacion === 5 && '¡Excelente! 🤩'}
              </p>
            )}
          </div>

          {/* Qué te gustó */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">¿Qué te gustó más de LILA?</label>
            <textarea
              value={formData.que_gusto}
              onChange={(e) => setFormData({ ...formData, que_gusto: e.target.value })}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Ejemplo: La rapidez de las respuestas, la generación de documentos..."
            />
          </div>

          {/* Qué mejorar */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">¿Qué podemos mejorar?</label>
            <textarea
              value={formData.que_mejorar}
              onChange={(e) => setFormData({ ...formData, que_mejorar: e.target.value })}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Tus sugerencias nos ayudan a mejorar..."
            />
          </div>

          {/* Mensaje adicional */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <MessageSquare className="inline mr-2" size={16} />
              Mensaje o sugerencia adicional
            </label>
            <textarea
              value={formData.mensaje}
              onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Cualquier otro comentario..."
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-4 rounded-xl font-semibold text-lg hover:from-purple-600 hover:to-indigo-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send size={20} />
                Enviar mi opinión
              </>
            )}
          </button>

          {/* CTA */}
          <div className="text-center pt-4 border-t border-white/10">
            <p className="text-slate-400 mb-3">¿Quieres seguir usando LILA?</p>
            <a 
              href="https://legal.linorttech.com/premium"
              className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:from-amber-600 hover:to-orange-600 transition-all"
            >
              ¡Solo $7.99 al mes!
            </a>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Linort Tech S.A. - Legal Tech Panamá
        </p>
      </div>
    </div>
  );
}
